import React from "react";
import FileTransferViewTable from "./fileTransferView";
import { Link } from "react-router-dom";
import "../util/tables.css";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import { Box } from "@mui/material";
import { useDataView } from "../../contexts/data.view.context";
import ViewEditFileTransfer from "./viewEditFileTransfer";
import AppDialog from "../util/appDialog";
import ShowLoadingBar from "../util/showLoadingBar";
import ViewDetailsIcon from "../util/detailsModal/viewDetailsIcon";
import { makeEnvCellRenderer } from "../shared/makeEnvCellRenderer";
import SyncDialog from "../util/confirmMatchDialog";
import { NotificationManager } from "react-notifications";
import * as API from "../api/index";
import { sliceByEnv, getEnvColumnValue, getEnvRow, getRowDiff, mergeFields } from "../shared/envUtils";
import { useFetchOnMount } from "../../hooks/useFetchOnMount";

const columns = [
  {
    id: "TRANSFER_ID",
    accessorKey: "TRANSFER_ID", //access nested data with dot notation
    header: "Transfer ID",
    size: 250,
    enableEditing: false,
    enableHiding: true,
  },
  {
    id: "PROJECT_ID",
    accessorKey: "PROJECT_ID",
    header: "Project ID",
    size: 150,
  },

  {
    accessorKey: "STATE",
    header: "State",
    size: 150,
    Edit: ({ cell, column, table }) => {
      let originalState = cell.row.original.STATE;
      const [stateValue, setState] = React.useState(originalState);

      const handleChange = (event) => {
        cell.row._valuesCache.STATE = event.target.value;
        setState(event.target.value);
      };

      const items = ["Active", "Closed", "Pending", "Locked"];

      const menuItems = items.map((item) => {
        return (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        );
      });

      return (
        <>
          <Box sx={{}}>
            <InputLabel
              id="demo-simple-select-label"
              size="small"
              shrink="true"
              sx={{
                bottom: -5,
              }}
            >
              State
            </InputLabel>

            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              variant="standard"
              value={stateValue}
              defaultValue={originalState}
              onChange={handleChange}
            >
              {menuItems}
            </Select>
          </Box>
        </>
      );
    },
  },
  {
    accessorKey: "TRANSFER_TYPE",
    header: "Transfer Type",
    size: 150,
  },
  {
    accessorKey: "RETENTION_DAYS",
    header: "Retention Days",
    size: 150,
  },
  {
    accessorKey: "COMMENTS",
    header: "Comments",
    size: 150,
  },
];

const FileTransfers = () => {
  const {
    fileTransfers,
    fetchFileTransfersFromAllEnv,
    REACT_APP_ENV,
    envColumn1,
    envColumn2,
    openDetailForm,
    editable,
    curRow,
    handleClickOpenDetail,
    handleDetailClose,
    onEditClick,
    openPromoteModal,
    setOpenPromoteModal,
    handleClickOpen,
    handleClose,
    envCell,
    openMatchModal,
    setOpenMatchModal,
    matchCell,
    matchSelectedFields,
    setMatchSelectedFields,
    matchDirection,
    setMatchDirection,
    handleMatchClickOpen,
    handleMatchClose,
  } = useDataView();

  let currentEnvConfig = [];
  let envSlice = { current: [], dev: [], test: [], prod: [] };

  const customCellAction = makeEnvCellRenderer({
    openPromoteModal, envCell, handleClickOpen, handleClose, handleConfirm: (...args) => handleConfirm(...args),
    handleMatchClickOpen,
  });

  const mainColumns = [
    {
      id: "TRANSFER_ID",
      accessorKey: "TRANSFER_ID", //access nested data with dot notation
      header: "Transfer ID",
      size: 250,
      enableEditing: false,
      enableHiding: true,
    },
    {
      id: "PROJECT_ID",
      accessorKey: "PROJECT_ID",
      header: "Project ID",
      size: 150,
      Cell: viewDetails(),
    },
    {
      accessorKey: "TRANSFER_TYPE",
      header: "Transfer Type",
      size: 150,
    },
    {
      id: "COMMANDS",
      accessorKey: "COMMANDS", //normal accessorKey
      header: "No of Commands",
      size: 200,
      enableEditing: false,
      //accessorFn: (row) => row,
      Cell: ({ row }) => {
        //const row = cell.getValue();
        return (
          <Link to={"/FileCommands/" + row.original.TRANSFER_ID}>
            {row.original.COMMANDS}
          </Link>
        );
      },
    },
    {
      accessorKey: "STATE",
      header: "State",
      size: 150,
    },
    {
      id: envColumn1,
      accessorKey: "envColumn1",
      header: envColumn1,
      size: 150,
      Cell: customCellAction,
    },
    {
      id: envColumn2,
      accessorKey: "envColumn2", //access nested data with dot notation
      header: envColumn2,
      size: 150,
      Cell: customCellAction,
    },
  ];

  let detailData = null;

  function viewDetails() {
    return function ({ cell, row }) {
      const cellValue = cell.getValue();
      const tooltip = "View Details";

      return (
        <>
          <ViewDetailsIcon
            row={row}
            tooltip={tooltip}
            onClick={handleClickOpenDetail}
          />
          {cellValue}

          <AppDialog
            open={openDetailForm}
            onClose={handleDetailClose}
            title="File Transfer Details"
            maxWidth="sm"
          >
            <ViewEditFileTransfer
              curRow={curRow.current}
              handleDetailClose={handleDetailClose}
              editable={editable}
              onEditClick={onEditClick}
              data={detailData}
            />
          </AppDialog>
        </>
      );
    };
  }
  // Pulls the target env's field values into the current env's record.
  // Scoped to the transfer record itself, same as the fields diffed —
  // nested commands aren't copied by this action.
  const handleMatchConfirm = (fields, direction) => {
    const current = matchCell.current && matchCell.current.cell.row.original;
    const target = matchCell.current && matchCell.current.targetRow;

    if (!current || !target) {
      setOpenMatchModal(false);
      return;
    }

    // _pkTransfer (leading underscore) is required by the backend upsert
    // to take the UPDATE branch instead of silently INSERTing a
    // duplicate. It's the DB's own surrogate primary key — NOT the same
    // value across environments — so it must never be in `fields`: it
    // always comes from whichever row (current/target) is actually
    // being written to, never the other side's copy.
    const fieldNames = ["TRANSFER_ID", "PROJECT_ID", "STATE", "TRANSFER_TYPE", "RETENTION_DAYS", "COMMENTS", "_pkTransfer"];
    const isPush = direction === "push";
    const targetEnvLabel = matchCell.current.cell.column.id;
    // Pull: base = current record, override checked fields with target's values, write back to current env.
    // Push: base = target record, override checked fields with current's values, write to target env.
    const merged = isPush
      ? mergeFields(fieldNames, target, current, fields)
      : mergeFields(fieldNames, current, target, fields);
    const destEnv = isPush ? targetEnvLabel : REACT_APP_ENV;

    API.AddRecordToOtherEnv(
      "integrations/cfg/transfers",
      destEnv,
      [merged]
    ).then((response) => {
      if (response.code === 200) {
        NotificationManager.success(
          isPush
            ? "Record synced to " + targetEnvLabel
            : "Record synced from " + targetEnvLabel,
          "File Transfer",
          10000
        );
        fetchFileTransfersFromAllEnv();
      } else {
        NotificationManager.error(response.message, "Failed!", 10000);
      }
    });

    setOpenMatchModal(false);
  };

  const handleConfirm = () => {
    const payload = [
      {
        TRANSFER_ID: envCell.current.row.original.TRANSFER_ID,
        PROJECT_ID: envCell.current.row.original.PROJECT_ID,
        STATE: "Pending",
        TRANSFER_TYPE: envCell.current.row.original.TRANSFER_TYPE,
        RETENTION_DAYS: envCell.current.row.original.RETENTION_DAYS,
        COMMENTS: envCell.current.row.original.COMMENTS,
      },
    ];

    const env = envCell.current.column.id;
    const cmdCount = envCell.current.row.original.COMMANDS;
    const transID = envCell.current.row.original.TRANSFER_ID;

    onSubmit(payload, env, cmdCount, transID);
    setOpenPromoteModal(false);
  };

  const onSubmit = (payload, env, cmdCount, transID) => {
    const resourcePath = "integrations/cfg/transfers";

    API.AddRecordToOtherEnv(resourcePath, env, payload).then((response) => {
      if (response.code === 200) {
        const resourcePath = "integrations/cfg/transfer/" + transID + "/cmds";

        if (parseInt(cmdCount) > 0) {
          API.GetDataFromAPI(resourcePath).then((data) => {
            data.map((el) => {
              delete el["_pkTransferCmd"];
              delete el["pkTransferCmd"];
              delete el["CREATED_BY"];
              delete el["CREATED_TS"];
              return el;
            });

            if (data !== null) {
              API.AddRecordToOtherEnv(resourcePath, env, data).then(
                (response) => {
                  if (response.code === 200) {
                    NotificationManager.success(
                      "Record added to " + env + " env",
                      "File Transfer",
                      10000
                    );
                    fetchFileTransfersFromAllEnv();
                  } else {
                    NotificationManager.error(
                      response.message,
                      "Only File Transfer is promoted. Commands failed!",
                      10000
                    );
                  }
                }
              );
            }
          });
        } else {
          NotificationManager.success(
            "Record added to " + env + " env",
            "File Transfer",
            10000
          );
          fetchFileTransfersFromAllEnv();
        }
      } else {
        NotificationManager.error(response.message, "Failed!", 10000);
      }
    });
  };

  useFetchOnMount(fileTransfers, fetchFileTransfersFromAllEnv);

  if (fileTransfers.length > 0) {
    envSlice = sliceByEnv(fileTransfers, REACT_APP_ENV);
    currentEnvConfig = envSlice.current;

    detailData = currentEnvConfig.map((file) => {
      return {
        _pkTransfer: file._pkTransfer,
        TRANSFER_ID: file.TRANSFER_ID,
        PROJECT_ID: file.PROJECT_ID,
        COMMANDS: file.COMMANDS,
        STATE: file.STATE,
        TRANSFER_TYPE: file.TRANSFER_TYPE,
        RETENTION_DAYS: file.RETENTION_DAYS,
        COMMENTS: file.COMMENTS,
      };
    });
  }

  let loading = false;
  if (currentEnvConfig.length === 0) {
    loading = true;
  }

  if (Boolean(loading)) {
    return <ShowLoadingBar title="LIST DATA TRANSFER" loading={loading} />;
  }

  return (
    <>
      <div className="cache-table">

        {currentEnvConfig.length > 0 && (
          <FileTransferViewTable
            columns={columns}
            data={currentEnvConfig.map((connection) => {
              const TRANSFER_ID = connection.TRANSFER_ID;
              const col1 = getEnvColumnValue({ envColumn: envColumn1, ...envSlice, idKey: "TRANSFER_ID", idValue: TRANSFER_ID });
              const col2 = getEnvColumnValue({ envColumn: envColumn2, ...envSlice, idKey: "TRANSFER_ID", idValue: TRANSFER_ID });
              const targetRow1 = getEnvRow({ envColumn: envColumn1, ...envSlice, idKey: "TRANSFER_ID", idValue: TRANSFER_ID });
              const targetRow2 = getEnvRow({ envColumn: envColumn2, ...envSlice, idKey: "TRANSFER_ID", idValue: TRANSFER_ID });

              return {
                _pkTransfer: connection._pkTransfer,
                TRANSFER_ID: connection.TRANSFER_ID,
                PROJECT_ID: connection.PROJECT_ID,
                COMMANDS: connection._cmdCount_,
                STATE: connection.STATE,
                TRANSFER_TYPE: connection.TRANSFER_TYPE,
                RETENTION_DAYS: connection.RETENTION_DAYS,
                COMMENTS: connection.COMMENTS,
                envColumn1: col1,
                envColumn2: col2,
                [`${envColumn1}__diff`]: { ...getRowDiff(connection, targetRow1), targetRow: targetRow1 },
                [`${envColumn2}__diff`]: { ...getRowDiff(connection, targetRow2), targetRow: targetRow2 },
                COMMANDS_TEXT: connection.COMMANDS,
              };
            })}
            mainColumns={mainColumns}
          />
        )}

        <SyncDialog
          open={openMatchModal}
          matchCell={matchCell}
          matchSelectedFields={matchSelectedFields}
          setMatchSelectedFields={setMatchSelectedFields}
          matchDirection={matchDirection}
          setMatchDirection={setMatchDirection}
          handleMatchClose={handleMatchClose}
          handleMatchConfirm={(...args) => handleMatchConfirm(...args)}
        />
      </div>
    </>
  );
};

export default FileTransfers;
